import React from 'react';
import { Space, theme } from 'antd';
import { FormOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export type SectionKey = 'form' | 'access';

type SectionTabsProps = {
  active: SectionKey;
  onChange: (section: SectionKey) => void;
  /** Number of people with access, shown as a chip on the "Ligipääs" pill */
  accessCount?: number;
  /** Draw attention to the "Ligipääs" pill (e.g. required responsible researcher not picked yet) */
  attention?: boolean;
};

/**
 * Prominent switcher between the "Vorm" and "Ligipääs" sections.
 * Rendered as large pill buttons (the active one filled with the primary color)
 * instead of subtle line tabs, so users notice that the page has two sections.
 */
export const SectionTabs: React.FC<SectionTabsProps> = ({
  active,
  onChange,
  accessCount = 0,
  attention = false,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation(['forms', 'local']);

  const pillStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.marginXS,
    height: 36,
    padding: `0 ${token.paddingMD}px`,
    borderRadius: 999,
    fontSize: token.fontSize,
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
    userSelect: 'none',
    background: isActive ? token.colorPrimary : token.colorBgContainer,
    color: isActive ? token.colorTextLightSolid : token.colorText,
    border: `1px solid ${isActive ? token.colorPrimary : token.colorBorderSecondary}`,
    boxShadow: isActive ? token.boxShadowTertiary : 'none',
    transition: 'all .2s',
  });

  const Pill: React.FC<{
    isActive: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }> = ({ isActive, onClick, children }) => (
    <span
      role='button'
      tabIndex={0}
      aria-pressed={isActive}
      style={pillStyle(isActive)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </span>
  );

  return (
    <Space size={8} wrap style={{ marginBottom: token.marginLG }}>
      <Pill isActive={active === 'form'} onClick={() => onChange('form')}>
        <FormOutlined />
        {t('forms.tab.form', 'Vorm')}
      </Pill>
      <Pill isActive={active === 'access'} onClick={() => onChange('access')}>
        <TeamOutlined />
        {t('forms.tab.access', 'Ligipääs')}
        {accessCount > 0 && (
          <span
            style={{
              background: active === 'access' ? 'rgba(255,255,255,0.3)' : token.colorPrimary,
              color: token.colorTextLightSolid,
              borderRadius: 999,
              fontSize: token.fontSizeSM,
              lineHeight: '16px',
              padding: '0 5px',
              minWidth: 16,
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            {accessCount}
          </span>
        )}
        {attention && active !== 'access' && (
          <span
            title={t(
              'forms.error.responsibleResearcherRequired',
              'Vastutav uurija on kohustuslik. Määra projektile vastutav uurija.'
            )}
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: token.colorError,
            }}
          />
        )}
      </Pill>
    </Space>
  );
};

export default SectionTabs;
